import { type PropsWithChildren, useState } from "react";
import { get } from "lodash";
import Collapsible from "react-collapsible";
import { type FieldError } from "react-hook-form";
import { spacing } from "../../../../../../../../stitches.config";
import { ButtonLink } from "../../../../../../../Common/ButtonLink";
import { Text, Textarea } from "../../../../../../../Inputs/react-hook-form";
import {
  getUiSchemaPropertyFormPath,
  useBuilderFormContext,
} from "../../../form/form";
import { WidgetSettingsContainer } from "../__styles__/WidgetSettings";
import type { InputSettingsFormProps } from "../FieldSettingsView";

type AdvancedSettingsProps = Pick<
  InputSettingsFormProps,
  "fieldPath" | "isReadOnly"
>;

export function AdvancedSettings({
  fieldPath,
  isReadOnly,
}: AdvancedSettingsProps) {
  const {
    register,
    formState: { errors },
  } = useBuilderFormContext();

  function registerWithErrorMessage(fieldName: string) {
    const path = getUiSchemaPropertyFormPath(fieldPath, fieldName);

    return {
      ...register(path),
      error: (get(errors, path) as FieldError | undefined)?.message,
    };
  }

  return (
    <WidgetSettingsContainer>
      <Textarea
        label="File type description"
        disabled={isReadOnly}
        required
        {...registerWithErrorMessage("fileTypeDescription")}
      />
      <Textarea
        label="File quantity description"
        disabled={isReadOnly}
        required
        {...registerWithErrorMessage("fileQuantityDescription")}
      />
      <Textarea
        label="File size description"
        disabled={isReadOnly}
        required
        {...registerWithErrorMessage("fileSizeDescription")}
      />
      <Text
        label="Document type abbreviation"
        disabled={isReadOnly}
        required
        {...registerWithErrorMessage("documentTypeAbbreviation")}
      />
      <Text
        label="Empty files message"
        disabled={isReadOnly}
        {...registerWithErrorMessage("emptyFilesMessage")}
      />
    </WidgetSettingsContainer>
  );
}

export function CollapsibleSection({ children }: PropsWithChildren<{}>) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Collapsible
      onOpen={() => setIsOpen(true)}
      onClosing={() => setIsOpen(false)}
      transitionTime={100}
      trigger={
        <div
          style={{
            pointerEvents: "none",
            marginTop: spacing.s.value,
          }}
        >
          <ButtonLink
            size="small"
            styleVariant="primary"
            href="#"
            rightIconName={isOpen ? "chevron-up" : "chevron-down"}
          >
            Advanced settings
          </ButtonLink>
        </div>
      }
    >
      {children}
    </Collapsible>
  );
}
